import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";

const TYPE_CLS = { chantier: "bg-anthracite text-ivory", phase: "bg-bordeaux-700 text-ivory", rdv: "bg-bronze text-ivory", echeance: "bg-greige text-anthracite" };
const TYPE_LABEL = { chantier: "Chantier", phase: "Phase", rdv: "Rendez-vous", echeance: "Échéance" };
const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export default function ChantiersCalendar({ onOpenProject }) {
  const [mode, setMode] = useState("month");
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setHours(12); return d; });
  const [events, setEvents] = useState([]);

  const range = useMemo(() => {
    if (mode === "week") { const d = new Date(cursor); const wd = (d.getDay() + 6) % 7; const s = addDays(d, -wd); return { start: s, end: addDays(s, 6) }; }
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1, 12); const wd = (first.getDay() + 6) % 7; const s = addDays(first, -wd);
    return { start: s, end: addDays(s, 41) };
  }, [mode, cursor]);

  useEffect(() => { api.get(`/chantiers/calendar?start=${iso(range.start)}&end=${iso(range.end)}`).then(r => setEvents(r.data.items)).catch(() => {}); }, [range]);

  const days = useMemo(() => { const out = []; for (let d = new Date(range.start); d <= range.end; d = addDays(d, 1)) out.push(new Date(d)); return out; }, [range]);
  const forDay = (d) => { const k = iso(d); return events.filter(e => e.start <= k && (e.end || e.start) >= k); };
  const shift = (n) => { const d = new Date(cursor); if (mode === "week") d.setDate(d.getDate() + 7 * n); else d.setMonth(d.getMonth() + n); setCursor(d); };
  const title = mode === "week" ? `Semaine du ${range.start.toLocaleDateString("fr-FR")}` : cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const today = iso(new Date());

  return (
    <div className="bg-white border border-black/10 rounded-rama-md p-4" data-testid="chantiers-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="p-2 rounded hover:bg-black/5" aria-label="Précédent" data-testid="cal-prev"><ChevronLeft size={16} /></button>
          <div className="font-display text-xl capitalize min-w-[220px] text-center" data-testid="cal-title">{title}</div>
          <button onClick={() => shift(1)} className="p-2 rounded hover:bg-black/5" aria-label="Suivant" data-testid="cal-next"><ChevronRight size={16} /></button>
          <button onClick={() => setCursor(new Date())} className="text-xs text-bordeaux-700 underline ml-2">Aujourd'hui</button>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden md:flex gap-2 text-xs mr-3">{Object.keys(TYPE_LABEL).map(t => <span key={t} className={`px-2 py-0.5 rounded ${TYPE_CLS[t]}`}>{TYPE_LABEL[t]}</span>)}</div>
          <button data-testid="cal-mode-week" onClick={() => setMode("week")} className={`text-xs px-3 py-1.5 rounded-full border ${mode === "week" ? "bg-anthracite text-ivory border-anthracite" : "bg-white border-black/15"}`}>Semaine</button>
          <button data-testid="cal-mode-month" onClick={() => setMode("month")} className={`text-xs px-3 py-1.5 rounded-full border ${mode === "month" ? "bg-anthracite text-ivory border-anthracite" : "bg-white border-black/15"}`}>Mois</button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px bg-black/10 rounded overflow-hidden text-xs">
        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map(d => <div key={d} className="bg-ivory p-2 font-mono-caption text-ink-soft text-center">{d}</div>)}
        {days.map(d => {
          const k = iso(d); const evs = forDay(d); const dim = mode === "month" && d.getMonth() !== cursor.getMonth();
          return (
            <div key={k} className={`bg-white p-1.5 ${mode === "week" ? "min-h-[260px]" : "min-h-[96px]"} ${dim ? "opacity-40" : ""}`} data-testid={`cal-day-${k}`}>
              <div className={`text-right mb-1 ${k === today ? "text-bordeaux-700 font-semibold" : "text-ink-soft"}`}>{d.getDate()}</div>
              <div className="space-y-1">
                {evs.slice(0, mode === "week" ? 20 : 3).map(e => (
                  <button key={e.id} onClick={() => e.project_id && onOpenProject?.(e.project_id)} title={e.titre} className={`w-full text-left truncate px-1.5 py-0.5 rounded ${TYPE_CLS[e.type]} ${e.project_id ? "hover:opacity-80" : "cursor-default"}`} data-testid={`cal-event-${e.id}`}>
                    {e.titre}{e.type === "phase" && e.pct != null ? ` · ${e.pct} %` : ""}
                  </button>
                ))}
                {evs.length > 3 && mode === "month" && <div className="text-ink-soft">+{evs.length - 3}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
